import { useLazyQuery, useMutation } from "@apollo/client";
import { clientNeoFact } from "@neonumy/api-clients";
import { useMemo, useEffect } from "react";
import {
  GET_PAYMENT,
  LIST_PAYMENTS,
  UPDATE_PAYMENT,
  DELETE_PAYMENT,
  CREATE_PAYMENT,
  LIST_SALE_DOCUMENTS_GROUPS,
  GET_PAYMENTS_XLSX,
  LIST_RETURN_PAYMENTS,
} from "queries/centralApi/payments";
import { onErrorGraphql } from "utils/onErrorGraphql";
import useCoreStore from "views/hooks/useCoreStore";

export default function usePaymentsApi(id?: ID, isReturn?: boolean) {
  const companySettings = useCoreStore(state => state.companySettings);
  const decimalPlaces = companySettings?.decimalPlaces;

  const [listPayments, { refetch, loading, data: listPaymentsData, error: listPaymentsError }] =
    useLazyQuery<ListPaymentsResponse, ListPaymentsVars>(
      isReturn ? LIST_RETURN_PAYMENTS : LIST_PAYMENTS,
      {
        client: clientNeoFact,
        fetchPolicy: "no-cache",
      },
    );

  useEffect(() => {
    if (listPaymentsError) onErrorGraphql(listPaymentsError);
  }, [listPaymentsError]);

  const listSource = isReturn
    ? listPaymentsData?.listReturnPayments
    : listPaymentsData?.listPayments;

  const dataSource: ListPaymentsResults =
    listSource?.results?.map(item => ({
      ...item,
      key: item.id,
    })) ?? [];

  const totalCount = listSource?.totalCount ?? 0;

  const [
    listSaleDocumentsGroups,
    { data: listSaleDocumentsGroupsData, error: listSaleDocumentsGroupsError },
  ] = useLazyQuery<ListInvoicesGroupsResponse, ListInvoicesGroupsVars>(LIST_SALE_DOCUMENTS_GROUPS, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listSaleDocumentsGroupsError) onErrorGraphql(listSaleDocumentsGroupsError);
  }, [listSaleDocumentsGroupsError]);

  const invociesGroupsOptions = useMemo(() => {
    const results = listSaleDocumentsGroupsData?.listSaleDocumentsGroups?.results;
    if (!results) return [];

    const newData = results.map(item => ({ ...item, key: item.id }));

    return newData.flatMap(({ invoices, id }) =>
      invoices.map(({ reference, client, totalPrice, type }) => ({
        label: `${reference} - ${Number(totalPrice).toFixed(decimalPlaces)}€ - ${client?.name}`,
        value: id,
        type: type?.value,
        client: client,
      })),
    );
  }, [listSaleDocumentsGroupsData, decimalPlaces]);

  const [getPayment, { refetch: refetchGetPayment, data: getPaymentData, error: getPaymentError }] =
    useLazyQuery<GetPaymentResponse, GetVarTypeID>(GET_PAYMENT, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (getPaymentError) onErrorGraphql(getPaymentError);
  }, [getPaymentError]);

  const payment = getPaymentData?.getPayment;

  const [getPaymentsXlsx, { error: getPaymentsXlsxError }] = useLazyQuery<
    GetPaymentsXlsxResponse,
    GetPaymentsXlsxVars
  >(GET_PAYMENTS_XLSX, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (getPaymentsXlsxError) onErrorGraphql(getPaymentsXlsxError);
  }, [getPaymentsXlsxError]);

  const [createUpdatePayment, { loading: createUpdatePaymentLoading }] = useMutation<
    CreateUpdatePaymentMutationResponse,
    CreateUpdatePaymentVars
  >(id ? UPDATE_PAYMENT : CREATE_PAYMENT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [deletePayment, { loading: deletePaymentLoading }] = useMutation<
    DeletePaymentResponse,
    GetVarTypeID
  >(DELETE_PAYMENT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  return {
    refetch,
    loading,
    payment,
    totalCount,
    dataSource,
    getPayment,
    listPayments,
    deletePayment,
    getPaymentsXlsx,
    refetchGetPayment,
    createUpdatePayment,
    deletePaymentLoading,
    invociesGroupsOptions,
    listSaleDocumentsGroups,
    createUpdatePaymentLoading,
  };
}
